import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'
import { buildCurrentProjectResourceUrl } from '@/features/static-resource/resourceCdn'
import { normalizeAppRuntimeEnv } from '@/config/domainPresets'
import { API_ENDPOINTS } from '@/config/apiEndpoints'
import { BaseWebUrl } from '@/utils/request/baseWebUrl'
import {
  pickText,
  requestJson,
  trimSlash,
  type JsonRecord,
} from '@/utils/request/apiRequest'

/** 已发布作品版本分页列表查询结果。 */
export interface PublishedProjectVersionListPageResult {
  items: PublishedProjectVersionItem[]
  count: number
  page: number
}

/** 已发布作品分页列表查询结果。 */
export interface PublishedCyGameListPageResult {
  items: PublishedProjectListItem[]
  count: number
  page: number
}

const webUrl = new BaseWebUrl()
const cgEndpoints = API_ENDPOINTS.cg
const RUNTIME_ENV = normalizeAppRuntimeEnv(import.meta.env.MODE || import.meta.env.VITE_APP_ENV)
const TEST_PUBLISHED_RESOURCE_CDN = 'https://testcdn.66rpg.com'

export interface PublishedProjectListItem {
  guid: string
  gindex: string
  projectName: string
  updateTime: string
}

export interface PublishedProjectVersionItem {
  version: string
  createTime: string
  gindex: string
  updateIntro: string
}

function normalizeDownloadUrlProtocol(url: string): string {
  const trimmed = url.trim()
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && /^http:\/\//i.test(trimmed)) {
    return trimmed.replace(/^http:\/\//i, 'https://')
  }
  return trimmed
}

function assertPublishedCyApiSuccess(data: JsonRecord, errorPrefix: string): JsonRecord {
  const successCandidates = [data.ret, data.status]
  const hasExplicitStatus = successCandidates.some((value) => value !== undefined && value !== null && value !== '')
  if (hasExplicitStatus) {
    const isSuccess = successCandidates.some((value) => String(value ?? '').trim() === '1')
    if (!isSuccess) {
      throw new Error(String(data.msg ?? errorPrefix))
    }
  }
  const payload = data.data
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error(`${errorPrefix}：返回数据格式错误`)
  }
  return payload as JsonRecord
}

function normalizeProject(record: JsonRecord): PublishedProjectListItem | null {
  const guid = pickText(record, ['guid', 'gguid', 'pguid'])
  if (!guid) return null
  return {
    guid,
    gindex: pickText(record, ['gindex', 'game_index', 'web_index', 'webIndex']),
    projectName: pickText(record, ['gname', 'proj_name', 'projectName', 'name']) || guid,
    updateTime: pickText(record, ['create_time_text', 'time', 'update_time', 'create_time']),
  }
}

function normalizeVersion(record: JsonRecord): PublishedProjectVersionItem | null {
  const version = pickText(record, ['version', 'proj_ver', 'project_version', 'pkg_ver', 'ver', 'id'])
  if (!version) return null
  const pubTime = pickText(record, ['pub_time', 'publish_time', 'update_time', 'time'])
  const pubTimeText = pickText(record, ['pub_time_text', 'create_time', 'publish_time_text'])
  return {
    version,
    createTime: pubTimeText || pubTime,
    gindex: pickText(record, ['gindex', 'game_index', 'web_index', 'webIndex']),
    updateIntro: pickText(record, ['log_intro', 'update_log', 'update_intro', 'intro', 'name', 'tool_version']),
  }
}

/**
 * 分页获取当前账号已发布作品列表。
 * @param sessionId 当前登录 sessionid。
 * @param page 页码，从 1 开始，默认第 1 页。
 * @returns 当前页作品列表与总数量。
 */
export async function getPublishedCyGameListPage(
  sessionId: string,
  page = 1,
): Promise<PublishedCyGameListPageResult> {
  const safePage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1
  const data = await requestJson(webUrl.baseCg, cgEndpoints.publishedCyGameList, {
    params: {
      sessionid: sessionId,
      page: safePage,
    },
    cacheBust: true,
    errorPrefix: '已发布作品列表获取失败',
  })
  const payload = assertPublishedCyApiSuccess(data, '已发布作品列表获取失败')
  const list = Array.isArray(payload.list) ? payload.list : []
  const items = list
    .filter((item): item is JsonRecord => !!item && typeof item === 'object')
    .map(normalizeProject)
    .filter((item): item is PublishedProjectListItem => item !== null)
  const count = Number(payload.count ?? items.length)
  return {
    items,
    count: Number.isFinite(count) && count >= 0 ? count : items.length,
    page: safePage,
  }
}

/**
 * 按 guid 从已发布作品列表中查找 gindex。
 * @param sessionId 当前登录 sessionid。
 * @param guid 作品 guid。
 * @returns 匹配到的 gindex；未找到时返回 null。
 */
export async function resolvePublishedGindexByGuid(sessionId: string, guid: string): Promise<string | null> {
  const targetGuid = guid.trim().toLowerCase()
  if (!targetGuid) return null
  let page = 1
  let serverPageSize = 0
  while (page <= 50) {
    const result = await getPublishedCyGameListPage(sessionId, page)
    if (serverPageSize <= 0 && result.items.length > 0) {
      serverPageSize = result.items.length
    }
    const matched = result.items.find((item) => item.guid.trim().toLowerCase() === targetGuid)
    if (matched?.gindex) return matched.gindex
    if (!result.items.length) return null
    if (serverPageSize <= 0 || page * serverPageSize >= result.count) return null
    page += 1
  }
  return null
}

/**
 * 分页获取指定作品的历史发布版本列表。
 * @param sessionId 当前登录 sessionid。
 * @param gindex 作品 gindex。
 * @param page 页码，从 1 开始，默认第 1 页。
 * @returns 当前页版本列表与总数量。
 */
export async function getPublishedProjectVersionsPage(
  sessionId: string,
  gindex: string | number,
  page = 1,
): Promise<PublishedProjectVersionListPageResult> {
  const cleanSessionId = String(sessionId ?? '').trim()
  if (!cleanSessionId) {
    throw new Error('sessionid 不能为空')
  }
  const cleanGindex = String(gindex ?? '').trim()
  if (!cleanGindex) {
    throw new Error('作品 gindex 不能为空')
  }
  const safePage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1
  const data = await requestJson(webUrl.baseCg, cgEndpoints.publishedCyGameVersionList, {
    params: {
      sessionid: cleanSessionId,
      gindex: cleanGindex,
      page: safePage,
    },
    cacheBust: true,
    errorPrefix: '已发布作品版本列表获取失败',
  })
  const payload = assertPublishedCyApiSuccess(data, '已发布作品版本列表获取失败')
  const list = Array.isArray(payload.list) ? payload.list : []
  const items = list
    .filter((item): item is JsonRecord => !!item && typeof item === 'object')
    .map(normalizeVersion)
    .filter((item): item is PublishedProjectVersionItem => item !== null)
  const count = Number(payload.count ?? items.length)
  return {
    items,
    count: Number.isFinite(count) && count >= 0 ? count : items.length,
    page: safePage,
  }
}

/**
 * 获取指定作品的全部历史发布版本列表（自动翻页）。
 * @param sessionId 当前登录 sessionid。
 * @param gindex 作品 gindex。
 * @returns 版本列表。
 */
export async function getPublishedProjectVersions(
  sessionId: string,
  gindex: string | number,
): Promise<PublishedProjectVersionItem[]> {
  let page = 1
  let serverPageSize = 0
  const all: PublishedProjectVersionItem[] = []
  while (page <= 50) {
    const result = await getPublishedProjectVersionsPage(sessionId, gindex, page)
    all.push(...result.items)
    if (!result.items.length) break
    if (serverPageSize <= 0 && result.items.length > 0) {
      serverPageSize = result.items.length
    }
    if (serverPageSize <= 0 || page * serverPageSize >= result.count) break
    page += 1
  }
  return all
}

/** 从发布 CDN 下载指定版本的 Map.bin。 */
export async function fetchPublishedMapBin(guid: string, version: string): Promise<Uint8Array> {
  const cleanGuid = String(guid || '').trim()
  const cleanVersion = String(version || '').trim()
  if (!cleanGuid || !cleanVersion) {
    throw new Error(`Map.bin 下载参数缺失：guid=${cleanGuid || '空'}，version=${cleanVersion || '空'}`)
  }
  const cdnBase = RUNTIME_ENV === 'prod' ? webUrl.baseCdn1 : TEST_PUBLISHED_RESOURCE_CDN
  const url = new URL(`${trimSlash(cdnBase)}/web/${encodeURIComponent(cleanGuid)}/${encodeURIComponent(cleanVersion)}/Map.bin`)
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`Map.bin 下载失败：HTTP ${res.status} · ${url.toString()}`)
  }
  return await BrowserFileUtils.responseToUint8Array(res)
}

/** 按 MD5 从共享资源 CDN 下载文件字节。 */
export async function fetchSharedResourceBytes(md5: string): Promise<Uint8Array> {
  const res = await fetch(buildCurrentProjectResourceUrl(md5))
  if (!res.ok) {
    throw new Error(`共享资源下载失败：HTTP ${res.status}`)
  }
  return await BrowserFileUtils.responseToUint8Array(res)
}

/** 下载已发布版本中的私有工程文件。 */
export async function downloadPublishedProjectFile(params: {
  guid: string
  sessionId: string
  toolVersion: string
  objectName: string
}): Promise<Uint8Array> {
  const signUrl = await getPublishedProjectFileSignUrl(params)
  const res = await fetch(signUrl)
  if (!res.ok) {
    throw new Error(`${params.objectName} 下载失败：HTTP ${res.status}`)
  }
  return await BrowserFileUtils.responseToUint8Array(res)
}

/** 获取已发布版本私有工程文件的签名下载地址。 */
export async function getPublishedProjectFileSignUrl(params: {
  guid: string
  sessionId: string
  toolVersion: string
  objectName: string
}): Promise<string> {
  const query = {
    guid: params.guid,
    version: params.toolVersion,
    sessionid: params.sessionId,
    object_name: BrowserFileUtils.stringtoHex(params.objectName),
  }
  const data = await requestJson(webUrl.baseCg, cgEndpoints.publishedProjectFileSign, {
    params: query,
    signed: true,
  })
  const payload = (data.data as JsonRecord | undefined) ?? data
  const signUrl = pickText(payload, ['sign_url', 'signUrl', 'url'])
  if (!signUrl) {
    throw new Error(`服务端未返回 ${params.objectName} 下载地址`)
  }
  return normalizeDownloadUrlProtocol(signUrl)
}

/** 按候选路径依次下载已发布版本的工程主包。 */
export async function downloadPublishedProjectFileFromCandidates(params: {
  guid: string
  sessionId: string
  toolVersion: string
  objectNames: string[]
}): Promise<Uint8Array> {
  const errors: string[] = []
  for (const objectName of params.objectNames) {
    try {
      return await downloadPublishedProjectFile({
        guid: params.guid,
        sessionId: params.sessionId,
        toolVersion: params.toolVersion,
        objectName,
      })
    } catch (error) {
      errors.push(error instanceof Error ? error.message : `${objectName} 下载失败`)
    }
  }
  throw new Error(`服务器工程主包下载失败：${errors.join('；')}`)
}
